import { SupportService } from '@acme/api/client';
import type { ResponseData_PagedResponse_TicketResponse } from '@acme/api';
import type { TicketResponse } from '@acme/domains';
import {
  TicketsFilters,
  TicketsTable,
  getTicketsApiParams,
  type TicketsFiltersValue,
} from '@acme/features';
import { Button } from 'flowbite-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PaginatedList,
  type PaginatedListMeta,
} from '@shared/ui/paginatedList';

export const TicketsListPage = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<TicketsFiltersValue>({
    search: '',
    statusId: undefined,
    categoryId: undefined,
    period: 'all',
  });
  const [totalCount, setTotalCount] = useState(0);
  const [pageItems, setPageItems] = useState<TicketResponse[]>([]);

  const apiParams = useMemo(() => getTicketsApiParams(filters), [filters]);

  const filterBySearch = (items: TicketResponse[]): TicketResponse[] => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((ticket) =>
      [
        ticket.number != null ? String(ticket.number) : '',
        ticket.applicationCode ?? '',
        ticket.subject ?? '',
        ticket.categoryId?.name ?? '',
        ticket.status?.name ?? '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  };

  const handleCreateTicket = () => {
    navigate('/help/tickets/create');
  };

  const handleRowClick = (ticketId: string) => {
    navigate(`/help/tickets/${ticketId}`);
  };

  const filteredItems = filterBySearch(pageItems);
  const displayCount = filters.search.trim()
    ? `${filteredItems.length} из ${totalCount}`
    : totalCount;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">
          Мои обращения
          {!!totalCount && (
            <span className="ml-2 text-base font-medium text-gray-500">
              ({displayCount})
            </span>
          )}
        </h1>
        <Button color="primary" onClick={handleCreateTicket}>
          Создать обращение
        </Button>
      </div>

      <TicketsFilters value={filters} onChange={setFilters} />

      <PaginatedList
        method={SupportService.getApiV1SupportTicketList}
        params={apiParams}
        extract={(response: ResponseData_PagedResponse_TicketResponse) => ({
          items: response.data?.items ?? [],
          totalCount: response.data?.totalCount ?? 0,
        })}
        onMetaChange={(meta: PaginatedListMeta<TicketResponse>) => {
          setTotalCount(meta.totalCount);
          setPageItems(meta.items);
        }}
      >
        {(items) => (
          <TicketsTable
            tickets={filterBySearch(items)}
            onRowClick={handleRowClick}
          />
        )}
      </PaginatedList>
    </div>
  );
};
